import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  Alert,
} from "@mui/material";
import SupervisorApi from "../apis/supervisorApi";
import { getDropdownSafeDialogSx } from "../utils/dialogLayering";
import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";
import { getTicketScopeLabel, getTicketScopeLabelPlural } from "../utils/mspUtils";

interface NewTicketDialogProps {
  open: boolean;
  onClose: () => void;
  queueName?: string;
  categoryName?: string;
  onTicketCreated?: () => void;
}

interface Company {
  id: string;
  name: string;
}

interface TicketOption {
  value: number;
  label: string;
}

interface ScopeOption {
  value: string;
  label: string;
  searchText?: string;
}

interface TicketCreateCapabilities {
  scopeLabelSingular: string;
  scopeLabelPlural: string;
  categoryLabelSingular: string;
  categoryLabelPlural: string;
  supportsCategorySelection: boolean;
  requiresScopeSelection: boolean;
  requiresCategorySelection: boolean;
}

const CREATE_CAPABILITIES_BY_MSP: Record<string, TicketCreateCapabilities> = {
  autotask: {
    scopeLabelSingular: "Queue",
    scopeLabelPlural: "Queues",
    categoryLabelSingular: "Category",
    categoryLabelPlural: "Categories",
    supportsCategorySelection: true,
    requiresScopeSelection: true,
    requiresCategorySelection: false,
  },
  connectwise: {
    scopeLabelSingular: "Board",
    scopeLabelPlural: "Boards",
    categoryLabelSingular: "",
    categoryLabelPlural: "",
    supportsCategorySelection: false,
    requiresScopeSelection: true,
    requiresCategorySelection: false,
  },
  halo: {
    scopeLabelSingular: "Team",
    scopeLabelPlural: "Teams",
    categoryLabelSingular: "Ticket Type",
    categoryLabelPlural: "Ticket Types",
    supportsCategorySelection: true,
    requiresScopeSelection: true,
    requiresCategorySelection: true,
  },
};

function resolveDefaultCreateCapabilities(mspType: string): TicketCreateCapabilities {
  const normalizedType = String(mspType || "").trim().toLowerCase();
  if (CREATE_CAPABILITIES_BY_MSP[normalizedType]) {
    return CREATE_CAPABILITIES_BY_MSP[normalizedType];
  }

  const scopeLabelSingular = getTicketScopeLabel(normalizedType);
  const scopeLabelPlural = getTicketScopeLabelPlural(normalizedType);
  return {
    scopeLabelSingular,
    scopeLabelPlural,
    categoryLabelSingular: "Category",
    categoryLabelPlural: "Categories",
    supportsCategorySelection: true,
    requiresScopeSelection: false,
    requiresCategorySelection: false,
  };
}

function normalizeCreateCapabilities(
  rawCapabilities: any,
  fallback: TicketCreateCapabilities
): TicketCreateCapabilities {
  return {
    scopeLabelSingular: String(
      rawCapabilities?.scopeLabelSingular || fallback.scopeLabelSingular
    ).trim(),
    scopeLabelPlural: String(
      rawCapabilities?.scopeLabelPlural || fallback.scopeLabelPlural
    ).trim(),
    categoryLabelSingular: String(
      rawCapabilities?.categoryLabelSingular ?? fallback.categoryLabelSingular
    ).trim(),
    categoryLabelPlural: String(
      rawCapabilities?.categoryLabelPlural ?? fallback.categoryLabelPlural
    ).trim(),
    supportsCategorySelection:
      typeof rawCapabilities?.supportsCategorySelection === "boolean"
        ? rawCapabilities.supportsCategorySelection
        : fallback.supportsCategorySelection,
    requiresScopeSelection:
      typeof rawCapabilities?.requiresScopeSelection === "boolean"
        ? rawCapabilities.requiresScopeSelection
        : fallback.requiresScopeSelection,
    requiresCategorySelection:
      typeof rawCapabilities?.requiresCategorySelection === "boolean"
        ? rawCapabilities.requiresCategorySelection
        : fallback.requiresCategorySelection,
  };
}

function getPriorityRank(option: TicketOption): number {
  const match = option.label.match(/priority\s*(\d+)/i);
  if (match?.[1]) {
    const parsed = Number(match[1]);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return option.value;
}

const DEFAULT_STATUS_OPTIONS: TicketOption[] = [
  { value: 1, label: "New" },
  { value: 8, label: "In Progress" },
  { value: 7, label: "Waiting Customer" },
  { value: 12, label: "Waiting Vendor" },
  { value: 5, label: "Complete" },
  { value: 11, label: "Escalate" },
];

const DEFAULT_PRIORITY_OPTIONS: TicketOption[] = [
  { value: 1, label: "Low" },
  { value: 2, label: "Medium" },
  { value: 3, label: "High" },
  { value: 4, label: "Critical" },
];

function normalizeScopeOptions(source: any[]): ScopeOption[] {
  const mapped = (Array.isArray(source) ? source : [])
    .map((option) => {
      const label = String(option?.name || option?.label || "").trim();
      if (!label) {
        return null;
      }
      return { value: label, label };
    })
    .filter((option): option is ScopeOption => Boolean(option));

  const unique = new Map<string, ScopeOption>();
  mapped.forEach((option) => {
    const key = option.value.toLowerCase();
    if (!unique.has(key)) {
      unique.set(key, option);
    }
  });

  return Array.from(unique.values()).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
}

function resolveConfiguredOptionValue(
  options: ScopeOption[],
  candidate: string
): string {
  const normalizedCandidate = String(candidate || "").trim().toLowerCase();
  if (!normalizedCandidate) {
    return "";
  }

  const matchedOption = options.find(
    (option) => option.value.toLowerCase() === normalizedCandidate
  );
  return matchedOption?.value || "";
}

const NewTicketDialog: React.FC<NewTicketDialogProps> = ({
  open,
  onClose,
  queueName,
  categoryName,
  onTicketCreated,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [companyID, setCompanyID] = useState("");
  const [status, setStatus] = useState(1);
  const [priority, setPriority] = useState(2);
  const [selectedQueueName, setSelectedQueueName] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [queueOptions, setQueueOptions] = useState<ScopeOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ScopeOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<TicketOption[]>(DEFAULT_STATUS_OPTIONS);
  const [priorityOptions, setPriorityOptions] = useState<TicketOption[]>(DEFAULT_PRIORITY_OPTIONS);
  const [assigneeOptions, setAssigneeOptions] = useState<ScopeOption[]>([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [defaultStatus, setDefaultStatus] = useState(1);
  const [defaultPriority, setDefaultPriority] = useState(2);
  const [mspType, setMspType] = useState("");
  const [ticketCreateCapabilities, setTicketCreateCapabilities] =
    useState<TicketCreateCapabilities>(resolveDefaultCreateCapabilities(""));
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingScopeOptions, setLoadingScopeOptions] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supervisorApi = useMemo(() => new SupervisorApi(), []);
  const queueSelectOptions = useMemo(() => queueOptions, [queueOptions]);
  const categorySelectOptions = useMemo(() => categoryOptions, [categoryOptions]);
  const resolvedCapabilities = useMemo(() => {
    const defaults = resolveDefaultCreateCapabilities(mspType);
    return normalizeCreateCapabilities(ticketCreateCapabilities, defaults);
  }, [mspType, ticketCreateCapabilities]);
  const scopeLabel = resolvedCapabilities.scopeLabelSingular || "Queue";
  const scopeLabelPlural =
    String(resolvedCapabilities.scopeLabelPlural || `${scopeLabel}s`).trim() ||
    `${scopeLabel}s`;
  const categoryLabel =
    String(resolvedCapabilities.categoryLabelSingular || "Category").trim() ||
    "Category";
  const categoryLabelPlural =
    String(
      resolvedCapabilities.categoryLabelPlural ||
        `${categoryLabel}s`
    ).trim() || `${categoryLabel}s`;
  const showQueueSelector = queueSelectOptions.length > 0;
  const showCategorySelector =
    resolvedCapabilities.supportsCategorySelection && categorySelectOptions.length > 0;
  const queueSelectionRequired = resolvedCapabilities.requiresScopeSelection;
  const categorySelectionRequired = resolvedCapabilities.requiresCategorySelection;
  const missingRequiredScopeConfiguration = queueSelectionRequired && !showQueueSelector;
  const missingRequiredCategoryConfiguration =
    categorySelectionRequired && !showCategorySelector;
  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        value: company.id,
        label: company.name,
      })),
    [companies]
  );
  const statusSelectOptions = useMemo(
    () =>
      statusOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    [statusOptions]
  );
  const prioritySelectOptions = useMemo(
    () =>
      priorityOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    [priorityOptions]
  );

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const response = await supervisorApi.getClientsList();
      if (Array.isArray(response)) {
        setCompanies(response);
      }
    } catch (err: any) {
      console.error("Error fetching companies:", err);
      setError("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, [supervisorApi]);

  const fetchTicketOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const effectiveQueueName = String(selectedQueueName || "").trim();
      const effectiveCategoryName = String(selectedCategoryName || "").trim();
      const response = await supervisorApi.getTicketCreateOptions({
        queueName: effectiveQueueName || undefined,
        categoryName: effectiveCategoryName || undefined,
      });
      const resolvedMspType = String(response?.mspType || "");
      setMspType(resolvedMspType);
      const defaultCapabilities = resolveDefaultCreateCapabilities(resolvedMspType);
      setTicketCreateCapabilities(
        normalizeCreateCapabilities(response?.capabilities, defaultCapabilities)
      );

      const fetchedStatusOptions = Array.isArray(response?.statusOptions)
        ? response.statusOptions
            .map((option: any) => ({
              value: Number(option?.value),
              label: String(option?.label || option?.value || ""),
            }))
            .filter((option: TicketOption) => Number.isFinite(option.value) && option.label)
        : [];

      const fetchedPriorityOptions = Array.isArray(response?.priorityOptions)
        ? response.priorityOptions
            .map((option: any) => ({
              value: Number(option?.value),
              label: String(option?.label || option?.value || ""),
            }))
            .filter((option: TicketOption) => Number.isFinite(option.value) && option.label)
            .sort((a: TicketOption, b: TicketOption) => {
              const rankDiff = getPriorityRank(a) - getPriorityRank(b);
              if (rankDiff !== 0) {
                return rankDiff;
              }
              return a.label.localeCompare(b.label);
            })
        : [];

      const resolvedStatusOptions =
        fetchedStatusOptions.length > 0 ? fetchedStatusOptions : DEFAULT_STATUS_OPTIONS;
      const resolvedPriorityOptions =
        fetchedPriorityOptions.length > 0 ? fetchedPriorityOptions : DEFAULT_PRIORITY_OPTIONS;

      setStatusOptions(resolvedStatusOptions);
      setPriorityOptions(resolvedPriorityOptions);

      const fetchedAssigneeOptions = Array.isArray(response?.assigneeOptions)
        ? response.assigneeOptions
            .map((option: any) => {
              const value = String(option?.id ?? option?.value ?? "").trim();
              const label = String(option?.label || "").trim();
              const email = String(option?.email || "").trim();
              if (!value || !label) {
                return null;
              }
              return {
                value,
                label,
                ...(email ? { searchText: email } : {}),
              };
            })
            .filter((option: ScopeOption | null): option is ScopeOption => Boolean(option))
        : [];
      setAssigneeOptions(fetchedAssigneeOptions);
      setAssigneeId((current) =>
        fetchedAssigneeOptions.some((option: ScopeOption) => option.value === current)
          ? current
          : ""
      );

      const resolvedDefaultStatus = Number.isFinite(Number(response?.defaultStatus))
        ? Number(response.defaultStatus)
        : resolvedStatusOptions[0].value;
      const resolvedDefaultPriority = Number.isFinite(Number(response?.defaultPriority))
        ? Number(response.defaultPriority)
        : resolvedPriorityOptions[0].value;

      setDefaultStatus(resolvedDefaultStatus);
      setDefaultPriority(resolvedDefaultPriority);
      setStatus(resolvedDefaultStatus);
      setPriority(resolvedDefaultPriority);
    } catch (err: any) {
      console.error("Error fetching ticket create options:", err);
      setStatusOptions(DEFAULT_STATUS_OPTIONS);
      setPriorityOptions(DEFAULT_PRIORITY_OPTIONS);
      setDefaultStatus(DEFAULT_STATUS_OPTIONS[0].value);
      setDefaultPriority(DEFAULT_PRIORITY_OPTIONS[1].value);
      setStatus(DEFAULT_STATUS_OPTIONS[0].value);
      setPriority(DEFAULT_PRIORITY_OPTIONS[1].value);
      setAssigneeOptions([]);
      setAssigneeId("");
      setMspType("");
      setTicketCreateCapabilities(resolveDefaultCreateCapabilities(""));
    } finally {
      setLoadingOptions(false);
    }
  }, [supervisorApi, selectedQueueName, selectedCategoryName]);

  const fetchScopeOptions = useCallback(async () => {
    setLoadingScopeOptions(true);
    try {
      const [queueResult, categoryResult] = await Promise.allSettled([
        supervisorApi.getTechnicianGroupList(),
        supervisorApi.getTicketCategoryList(),
      ]);

      const normalizedQueues =
        queueResult.status === "fulfilled"
          ? normalizeScopeOptions(queueResult.value)
          : [];
      const normalizedCategories =
        categoryResult.status === "fulfilled"
          ? normalizeScopeOptions(categoryResult.value)
          : [];

      if (queueResult.status === "rejected") {
        console.error("Error fetching queue options:", queueResult.reason);
      }
      if (categoryResult.status === "rejected") {
        console.error("Error fetching category options:", categoryResult.reason);
      }

      setQueueOptions(normalizedQueues);
      setCategoryOptions(normalizedCategories);
      setSelectedQueueName((currentValue) => {
        const configuredCurrentValue = resolveConfiguredOptionValue(
          normalizedQueues,
          currentValue
        );
        if (configuredCurrentValue) {
          return configuredCurrentValue;
        }

        const configuredContextValue = resolveConfiguredOptionValue(
          normalizedQueues,
          queueName || ""
        );
        if (configuredContextValue) {
          return configuredContextValue;
        }

        return "";
      });
      setSelectedCategoryName((currentValue) => {
        const configuredCurrentValue = resolveConfiguredOptionValue(
          normalizedCategories,
          currentValue
        );
        if (configuredCurrentValue) {
          return configuredCurrentValue;
        }

        const configuredContextValue = resolveConfiguredOptionValue(
          normalizedCategories,
          categoryName || ""
        );
        if (configuredContextValue) {
          return configuredContextValue;
        }

        return "";
      });
    } catch (err: any) {
      console.error("Error fetching queue/category options:", err);
      setQueueOptions([]);
      setCategoryOptions([]);
      setSelectedQueueName("");
      setSelectedCategoryName("");
    } finally {
      setLoadingScopeOptions(false);
    }
  }, [supervisorApi, queueName, categoryName]);

  // Fetch create dependencies when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    fetchCompanies();
    fetchScopeOptions();
  }, [open, queueName, categoryName, fetchCompanies, fetchScopeOptions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    fetchTicketOptions();
  }, [open, fetchTicketOptions]);

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!companyID) {
      setError("Account/Company is required");
      return;
    }
    if (queueSelectionRequired && !String(selectedQueueName || "").trim()) {
      setError(`${scopeLabel} is required`);
      return;
    }
    if (categorySelectionRequired && !String(selectedCategoryName || "").trim()) {
      setError(`${categoryLabel} is required`);
      return;
    }
    if (missingRequiredScopeConfiguration) {
      setError(
        `No configured ${scopeLabelPlural.toLowerCase()} available for ticket creation`
      );
      return;
    }
    if (missingRequiredCategoryConfiguration) {
      setError(
        `No ${categoryLabelPlural.toLowerCase()} available for ticket creation`
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await supervisorApi.createTicket({
        title: title.trim(),
        description: description.trim(),
        companyID,
        status,
        priority,
        queueName: String(selectedQueueName || "").trim() || undefined,
        categoryName: String(selectedCategoryName || "").trim() || undefined,
        assigneeId: String(assigneeId || "").trim() || undefined,
      });

      // Reset form
      setTitle("");
      setDescription("");
      setCompanyID("");
      setStatus(defaultStatus);
      setPriority(defaultPriority);
      setSelectedQueueName("");
      setSelectedCategoryName("");
      setAssigneeId("");

      // Notify parent and close
      if (onTicketCreated) {
        onTicketCreated();
      }
      onClose();
    } catch (err: any) {
      console.error("Error creating ticket:", err);
      setError(err.response?.data?.error || "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTitle("");
      setDescription("");
      setCompanyID("");
      setStatus(defaultStatus);
      setPriority(defaultPriority);
      setSelectedQueueName("");
      setSelectedCategoryName("");
      setAssigneeId("");
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={getDropdownSafeDialogSx({ borderRadius: 2 })}
    >
      <DialogTitle
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 600,
          fontSize: "20px",
          color: "#5A5E73",
        }}
      >
        Create New Ticket
      </DialogTitle>
      <DialogContent
        sx={{
          overflow: "visible", // Allow dropdown to overflow
          position: "relative", // Create positioning context for absolute menu
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "var(--theo-font-sans)",
              },
              "& .MuiInputBase-root": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={7}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={loading}
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "var(--theo-font-sans)",
              },
              "& .MuiInputBase-root": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          />
          <LayeredAutocompleteSelect
            label="Account/Company"
            required
            value={companyID}
            onChange={setCompanyID}
            disabled={loading || loadingCompanies}
            loading={loadingCompanies}
            noOptionsText={loadingCompanies ? "Loading companies..." : "No companies available"}
            options={companyOptions}
            listboxMaxHeight={420}
          />
          {showQueueSelector && (
            <LayeredAutocompleteSelect
              label={scopeLabel}
              value={selectedQueueName}
              onChange={setSelectedQueueName}
              disabled={loading || loadingScopeOptions || loadingOptions}
              loading={loadingScopeOptions}
              required={queueSelectionRequired}
              noOptionsText={
                loadingScopeOptions
                  ? `Loading ${scopeLabelPlural.toLowerCase()}...`
                  : `No ${scopeLabelPlural.toLowerCase()} available`
              }
              options={queueSelectOptions}
              listboxMaxHeight={420}
            />
          )}
          {showCategorySelector && (
            <LayeredAutocompleteSelect
              label={categoryLabel}
              value={selectedCategoryName}
              onChange={setSelectedCategoryName}
              disabled={loading || loadingScopeOptions || loadingOptions}
              loading={loadingScopeOptions}
              required={categorySelectionRequired}
              noOptionsText={
                loadingScopeOptions
                  ? `Loading ${categoryLabelPlural.toLowerCase()}...`
                  : `No ${categoryLabelPlural.toLowerCase()} available`
              }
              options={categorySelectOptions}
              listboxMaxHeight={420}
            />
          )}
          <LayeredAutocompleteSelect
            label="Status"
            value={String(status)}
            onChange={(nextValue) => setStatus(Number(nextValue))}
            disabled={loading || loadingOptions}
            options={statusSelectOptions}
            disableClearable
          />
          <LayeredAutocompleteSelect
            label="Priority"
            value={String(priority)}
            onChange={(nextValue) => setPriority(Number(nextValue))}
            disabled={loading || loadingOptions}
            options={prioritySelectOptions}
            disableClearable
          />
          {assigneeOptions.length > 0 && (
            <LayeredAutocompleteSelect
              label="Assigned To"
              value={assigneeId}
              onChange={setAssigneeId}
              disabled={loading || loadingOptions}
              loading={loadingOptions}
              noOptionsText={
                loadingOptions ? "Loading technicians..." : "No technicians available"
              }
              options={assigneeOptions}
              listboxMaxHeight={420}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            textTransform: "none",
            color: "#5A5E73",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !title.trim() ||
            !companyID ||
            missingRequiredScopeConfiguration ||
            missingRequiredCategoryConfiguration ||
            (queueSelectionRequired && !String(selectedQueueName || "").trim()) ||
            (categorySelectionRequired && !String(selectedCategoryName || "").trim())
          }
          sx={{
            fontFamily: "var(--theo-font-sans)",
            textTransform: "none",
            backgroundColor: "#A084E8",
            "&:hover": {
              backgroundColor: "#8B6FD6",
            },
            "&:disabled": {
              backgroundColor: "#E0E0E0",
            },
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Create Ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewTicketDialog;
